import { getAuth } from "firebase/auth";
import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import { useStreams } from "../hooks/useStreams";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { trackClick } from "../utils/analytics";
import {
  History,
  Trash2,
  Clock,
  Search,
  FileText,
  AlertCircle,
  Loader2,
  Download,
  Copy,
  Check,
  Pencil,
  Eye,
  EyeOff,
  ArrowUpDown,
  X
} from "lucide-react";
import { useAdvancedSettings } from "../utils/advancedSettings";
import { buildDocxBlob, downloadBlob, safeFileName, transcriptAsText } from "../utils/transcriptExport";

const API_BASE_URL = "https://api.deafassistant.com";

interface HistoryItem {
  id: string;
  transcript: string;
  startTimestamp: string;
  inputLanguage?: string;
  title?: string | null;
  description?: string | null;
  isHidden?: boolean;
}

type SortOrder = "newest" | "oldest";
const SORT_KEY = "transcriptHistorySort";

const readSavedSort = (): SortOrder => {
  try {
    return localStorage.getItem(SORT_KEY) === "oldest" ? "oldest" : "newest";
  } catch {
    return "newest";
  }
};

// The API returns timestamps with 7 fractional digits and no zone; the first 23 characters parse reliably.
const parseTimestamp = (value: string): Date => new Date(value.substring(0, 23));

const TranscriptHistory: React.FC = () => {
  const { t } = useTranslation();
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);

  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>(readSavedSort);
  const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadMenuId, setDownloadMenuId] = useState<string | null>(null);
  const [editing, setEditing] = useState<HistoryItem | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [streamData, setStreamData] = useState({
    streamName: "",
    uid: "",
  });

  const STREAM_NAME = streamData.streamName;
  const uid = streamData.uid || user?.uid;
  const { settings: advanced } = useAdvancedSettings(user?.uid);

  const { selected, loading: streamsLoading } = useStreams();
  useEffect(() => {
    if (!user || streamsLoading) return;
    if (selected) setStreamData({ streamName: selected.name, uid: user.uid });
    else setIsLoading(false); // no stream, so there is no history to wait for
  }, [user, selected, streamsLoading]);

  const formatTimeAgo = (creationTimeString: string): string => {
    const cleanDateString = creationTimeString.substring(0, 23);
    const pastDate = new Date(cleanDateString);
    if (isNaN(pastDate.getTime())) return "Invalid time";

    const now = new Date();
    const diffInSeconds = Math.floor(
      (now.getTime() - pastDate.getTime()) / 1000
    );
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) return "just now";
    if (diffInMinutes < 60)
      return `${diffInMinutes}m ago`;
    if (diffInHours < 24)
      return `${diffInHours}h ago`;
    if (diffInDays < 7)
      return `${diffInDays}d ago`;

    return pastDate.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const fetchHistory = async (): Promise<void> => {
    if (!STREAM_NAME || !uid) {
      setError("Missing stream configuration");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const url = new URL(`${API_BASE_URL}/history/GetStreamHistory`);
      url.search = new URLSearchParams({
        streamName: STREAM_NAME,
        uid,
        includeHidden: "true", // the owner sees hidden transcripts (badged); public pages do not
      }).toString();

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data: HistoryItem[] = await response.json();
      setHistoryItems(data || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
      setError("Could not load transcript history.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (item: HistoryItem): string => {
    const date = parseTimestamp(item.startTimestamp);
    if (isNaN(date.getTime())) return "Unknown date";
    return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  };

  // What the person sees in the list: the title if there is one, otherwise the date and stream name.
  const itemLabel = (item: HistoryItem): string =>
    item.title?.trim() ? item.title.trim() : `${formatDateTime(item)} - ${STREAM_NAME}`;

  const showNotice = (kind: "ok" | "error", text: string) => {
    setNotice({ kind, text });
    window.setTimeout(() => setNotice(null), kind === "ok" ? 3000 : 8000);
  };

  const downloadItem = async (item: HistoryItem, format: "txt" | "docx"): Promise<void> => {
    setDownloadMenuId(null);
    trackClick(`button_click-download_transcript_${format}`);
    const base = safeFileName(item.title?.trim() || `transcript-${formatDateTime(item)}`);
    const meta = { title: itemLabel(item), description: item.description, date: formatDateTime(item), language: item.inputLanguage };
    try {
      if (format === "txt") {
        downloadBlob(new Blob([transcriptAsText(item.transcript, meta)], { type: "text/plain;charset=utf-8" }), `${base}.txt`);
      } else {
        downloadBlob(await buildDocxBlob(item.transcript, meta), `${base}.docx`);
      }
    } catch (err) {
      console.error("Download failed:", err);
      showNotice("error", "Could not create the download. Try the .txt option.");
    }
  };

  const copyItem = async (item: HistoryItem): Promise<void> => {
    trackClick("button_click-copy_transcript");
    const text = item.transcript;
    let copied = false;
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch {
      // Clipboard API unavailable or refused (older browsers, insecure context, very large text): try the textarea method.
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
      document.body.removeChild(area);
    }
    if (copied) {
      setCopiedId(item.id);
      window.setTimeout(() => setCopiedId((current) => (current === item.id ? null : current)), 2000);
      showNotice("ok", "Copied to the clipboard.");
    } else {
      // Never present a partial copy as complete: say it failed and offer the file instead.
      showNotice("error", "Your browser could not copy this transcript. Use Download instead.");
    }
  };

  const openEdit = (item: HistoryItem) => {
    setEditing(item);
    setEditTitle(item.title ?? "");
    setEditDescription(item.description ?? "");
  };

  const saveEdit = async (): Promise<void> => {
    if (!editing) return;
    setIsSavingEdit(true);
    try {
      const response = await fetch(`${API_BASE_URL}/history/HistoryRecordUpdate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ streamName: STREAM_NAME, uid, id: Number(editing.id), title: editTitle, description: editDescription }),
      });
      if (!response.ok) throw new Error(await response.text());
      setHistoryItems((prev) =>
        prev.map((item) =>
          item.id === editing.id
            ? { ...item, title: editTitle.trim() || null, description: editDescription.trim() || null }
            : item
        )
      );
      setEditing(null);
    } catch (err) {
      console.error("Failed to save title:", err);
      showNotice("error", "Could not save the title and note.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const toggleHidden = async (item: HistoryItem): Promise<void> => {
    const hide = !item.isHidden;
    trackClick(hide ? "button_click-hide_transcript" : "button_click-unhide_transcript");
    try {
      const url = new URL(`${API_BASE_URL}/history/HistoryRecordSetHidden`);
      url.search = new URLSearchParams({ streamName: STREAM_NAME!, uid: uid!, id: item.id, hidden: String(hide) }).toString();
      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("API Error");
      setHistoryItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isHidden: hide } : i)));
    } catch (err) {
      console.error("Failed to change visibility:", err);
      showNotice("error", hide ? "Could not hide the transcript." : "Could not unhide the transcript.");
    }
  };

  const changeSort = (order: SortOrder) => {
    setSortOrder(order);
    try {
      localStorage.setItem(SORT_KEY, order);
    } catch {
      // Storage can be blocked; the choice then lasts for this visit only.
    }
  };

  const deleteItem = async (item: HistoryItem): Promise<void> => {
    const historyId = item.id;
    trackClick("button_click-delete_transcript");
    if (!window.confirm(`Delete "${itemLabel(item)}"? This cannot be undone.`)) return;

    try {
      const url = new URL(`${API_BASE_URL}/history/HistoryRecordDel`);
      url.search = new URLSearchParams({
        streamName: STREAM_NAME!,
        uid: uid!,
        id: historyId,
      }).toString();

      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("API Error");

      setHistoryItems((prev) => prev.filter((item) => item.id !== historyId));
      trackClick("success-delete_transcript");
    } catch (err) {
      console.error("Failed to delete history item:", err);
      alert("Could not delete the transcript.");
    }
  };

  const deleteAll = async (): Promise<void> => {
    trackClick("button_click-delete_all_transcripts");
    if (!window.confirm(t("transcript.deleteAllConfirm"))) return;

    try {
      setIsDeleting(true);
      const url = new URL(`${API_BASE_URL}/history/HistoryStreamDel`);
      url.search = new URLSearchParams({
        streamName: STREAM_NAME!,
        uid: uid!,
      }).toString();

      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("API Error");

      await fetchHistory();
      trackClick("success-delete_all_transcripts");
    } catch (err) {
      console.error("Failed to delete all history:", err);
      alert("Could not delete all transcripts.");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!loading && user && STREAM_NAME) {
      fetchHistory();
    }
  }, [STREAM_NAME, uid, loading, user]);

  const query = searchQuery.toLowerCase();
  const filteredItems = historyItems
    .filter(item =>
      item.transcript.toLowerCase().includes(query) ||
      (item.title ?? "").toLowerCase().includes(query) ||
      (item.description ?? "").toLowerCase().includes(query)
    )
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const diff = parseTimestamp(a.item.startTimestamp).getTime() - parseTimestamp(b.item.startTimestamp).getTime();
      // Equal timestamps keep the order the API returned them, in either direction.
      if (diff === 0) return a.index - b.index;
      return sortOrder === "newest" ? -diff : diff;
    })
    .map(({ item }) => item);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="transcript-history" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                  {t("transcript.title")}
                </h1>
                <p className="text-slate-500 mt-1 text-lg">
                  Archive of all live transcriptions from <span className="font-bold text-slate-700">{STREAM_NAME}</span>
                </p>
              </div>
              <div className="flex gap-3">
                {/* <button className="btn-secondary flex items-center gap-2">
                  <Download size={18} />
                  Export All
                </button> */}
                {historyItems.length > 0 && (
                  <button
                    onClick={deleteAll}
                    disabled={isDeleting}
                    className="btn-danger flex items-center gap-2 group"
                  >
                    {isDeleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} className="text-red-400 group-hover:text-red-600 transition-colors" />}
                    {t("transcript.deleteAll")}
                  </button>
                )}
              </div>
            </header>

            {/* Search and Filters */}
            <div className="bg-white rounded-3xl p-4 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search transcripts..."
                  className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <label className="px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100 text-slate-700 text-sm font-bold flex items-center gap-2">
                  <ArrowUpDown size={18} className="text-blue-600" aria-hidden="true" />
                  <span className="sr-only md:not-sr-only">Sort</span>
                  <select
                    value={sortOrder}
                    onChange={(e) => changeSort(e.target.value as SortOrder)}
                    className="bg-transparent outline-none font-bold text-slate-800"
                    aria-label="Sort transcripts"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </label>
                <div className="px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100 text-slate-700 text-sm font-bold flex items-center gap-2">
                  <FileText size={18} className="text-blue-500" />
                  {historyItems.length} Total
                </div>
              </div>
            </div>

            {/* Content Area */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-24 space-y-4">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                <p className="text-slate-500 font-medium">Loading history...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-100 rounded-[2rem] p-12 text-center max-w-md mx-auto">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Failed to load history</h3>
                <p className="text-slate-500 mb-6">{error}</p>
                <button onClick={fetchHistory} className="btn-primary">Try Again</button>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="bg-white rounded-[2rem] p-16 text-center shadow-sm border border-slate-100">
                <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center text-slate-300 mx-auto mb-6">
                  <History size={40} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">No transcripts found</h3>
                <p className="text-slate-500 max-w-sm mx-auto">
                  {searchQuery ? "No matches found for your search query. Try different keywords." : "When you start a live stream, transcripts will appear here automatically."}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className={`group bg-white border rounded-[2rem] p-6 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 flex flex-col sm:flex-row items-start gap-4 sm:gap-6 ${item.isHidden ? "border-amber-200 bg-amber-50/40" : "border-slate-100"}`}
                  >
                    <div className="shrink-0 w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 font-bold group-hover:scale-110 transition-transform">
                      <FileText size={20} />
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      {item.title?.trim() && (
                        <h3 className="text-xl font-bold text-slate-900 break-words">{item.title}</h3>
                      )}
                      {item.description?.trim() && (
                        <p className="text-slate-700 text-base break-words">{item.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-slate-700">
                        <span className="flex items-center gap-1.5">
                          <Clock size={16} aria-hidden="true" />
                          <time dateTime={item.startTimestamp.substring(0, 23)}>{formatDateTime(item)}</time>
                        </span>
                        <span className="text-slate-600">({formatTimeAgo(item.startTimestamp)})</span>
                        {item.inputLanguage && (
                          <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 text-sm uppercase" title="Spoken language">
                            {item.inputLanguage}
                          </span>
                        )}
                        <span className="text-blue-800">Transcript #{String(item.id).slice(-4)}</span>
                        {item.isHidden && (
                          <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-sm font-bold">Hidden</span>
                        )}
                      </div>
                      <p className="text-slate-800 text-lg leading-relaxed whitespace-pre-wrap break-words">
                        {item.transcript}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 relative">
                      <button
                        onClick={() => copyItem(item)}
                        className="p-3 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl transition-all"
                        title="Copy transcript"
                        aria-label={`Copy transcript: ${itemLabel(item)}`}
                      >
                        {copiedId === item.id ? <Check size={20} className="text-green-700" /> : <Copy size={20} />}
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setDownloadMenuId(downloadMenuId === item.id ? null : item.id)}
                          className="p-3 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl transition-all"
                          title="Download transcript"
                          aria-label={`Download transcript: ${itemLabel(item)}`}
                          aria-haspopup="menu"
                          aria-expanded={downloadMenuId === item.id}
                        >
                          <Download size={20} />
                        </button>
                        {downloadMenuId === item.id && (
                          <div role="menu" className="absolute right-0 z-20 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                            <button role="menuitem" className="w-full text-left px-4 py-3 text-slate-800 hover:bg-slate-50" onClick={() => downloadItem(item, "docx")}>Word document (.docx)</button>
                            <button role="menuitem" className="w-full text-left px-4 py-3 text-slate-800 hover:bg-slate-50" onClick={() => downloadItem(item, "txt")}>Plain text (.txt)</button>
                          </div>
                        )}
                      </div>
                      {advanced.advancedHistory && (
                        <>
                          <button
                            onClick={() => openEdit(item)}
                            className="p-3 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl transition-all"
                            title="Edit title and note"
                            aria-label={`Edit title and note: ${itemLabel(item)}`}
                          >
                            <Pencil size={20} />
                          </button>
                          <button
                            onClick={() => toggleHidden(item)}
                            className="p-3 text-slate-600 hover:text-amber-800 hover:bg-amber-50 rounded-xl transition-all"
                            title={item.isHidden ? "Unhide (show in public views)" : "Hide from public views"}
                            aria-label={`${item.isHidden ? "Unhide" : "Hide"} transcript: ${itemLabel(item)}`}
                          >
                            {item.isHidden ? <Eye size={20} /> : <EyeOff size={20} />}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => deleteItem(item)}
                        className="p-3 text-slate-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all"
                        title="Delete"
                        aria-label={`Delete transcript: ${itemLabel(item)}`}
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notice && (
            <div
              role={notice.kind === "error" ? "alert" : "status"}
              className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl shadow-lg text-sm font-bold ${notice.kind === "error" ? "bg-red-700 text-white" : "bg-slate-900 text-white"}`}
            >
              {notice.text}
            </div>
          )}

          {editing && (
            <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="edit-title-heading"
                className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl space-y-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <h2 id="edit-title-heading" className="text-xl font-bold text-slate-900">Title and note</h2>
                  <button onClick={() => setEditing(null)} aria-label="Close" className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
                </div>
                <div>
                  <label htmlFor="history-title" className="font-bold text-slate-800 block">Title</label>
                  <input
                    id="history-title"
                    autoFocus
                    maxLength={200}
                    placeholder="Opening Remarks - Monday"
                    className="mt-1 w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="history-description" className="font-bold text-slate-800 block">Note</label>
                  <textarea
                    id="history-description"
                    rows={3}
                    maxLength={2000}
                    placeholder="A short description shown under the title"
                    className="mt-1 w-full bg-slate-50 border border-slate-300 rounded-xl py-2 px-3"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                  />
                  <p className="text-slate-600 text-sm mt-1">Leave both empty to show the date and time instead.</p>
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
                  <button onClick={saveEdit} disabled={isSavingEdit} className="btn-primary">{isSavingEdit ? "Saving..." : "Save"}</button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TranscriptHistory;
